import { useEffect, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Receipt } from "lucide-react";

import { api } from "@/lib/api";
import { fmtDate } from "@/lib/format";
import { AdminPage, Card, ChipRow, Gate, Meta, Pager } from "@/components/admin-ui";
import { EmptyState, ErrorState, Field, Loading, Pill } from "@/components/ui";

export default function AdminAudit() {
  return <Gate perm="audit" testId="admin-audit-forbidden"><Inner /></Gate>;
}

const isDate = (s) => /^\d{4}-\d{2}-\d{2}$/.test(s);

function Inner() {
  const [actor, setActor] = useState("");
  const [action, setAction] = useState("");
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const [page, setPage] = useState(1);
  const p = new URLSearchParams();
  if (actor) p.set("actor_id", actor);
  if (action.trim()) p.set("action", action.trim());
  if (isDate(from)) p.set("date_from", from);
  if (isDate(to)) p.set("date_to", to);
  const filterKey = p.toString();
  useEffect(() => setPage(1), [filterKey]); // reset paging whenever a filter changes
  p.set("page", String(page));
  const qs = p.toString();
  const { data, isLoading, isError, error, refetch } = useQuery({ queryKey: ["admin-audit", qs], queryFn: () => api(`/admin/audit?${qs}`), placeholderData: (prev) => prev });
  return (
    <AdminPage title="Audit log" testId="admin-audit-screen">
      <ChipRow options={[{ id: "", label: "All staff" }, ...(data?.actors ?? []).map((a) => ({ id: a.id, label: a.name }))]} value={actor} onChange={setActor} testIdPrefix="admin-audit-actor" />
      <div className="grid gap-x-3 sm:grid-cols-[2fr_1fr_1fr]">
        <Field testId="admin-audit-action-input" label="Action contains" value={action} onChange={(e) => setAction(e.target.value)} placeholder="e.g. member_suspend" />
        <Field testId="admin-audit-from-input" label="From" type="date" value={from} onChange={(e) => setFrom(e.target.value)} />
        <Field testId="admin-audit-to-input" label="To" type="date" value={to} onChange={(e) => setTo(e.target.value)} />
      </div>
      {isLoading ? <Loading /> : isError ? <ErrorState testId="admin-audit-error" message={error.message} onRetry={() => refetch()} /> : data.logs.length ? (
        <>
          {data.logs.map((l) => (
            <Card key={l.id} testId={`admin-audit-row-${l.id}`}>
              <div className="flex justify-between gap-2"><span className="font-mono text-sm font-bold">{l.action}</span><Pill label={l.target_type ?? "-"} /></div>
              <Meta>{l.actor_name} ({l.actor_roles.join(", ")}) · {fmtDate(l.created_at)}</Meta>
              {Object.keys(l.details).length ? <p className="mt-1 line-clamp-3 break-all font-mono text-xs text-muted-foreground">{JSON.stringify(l.details)}</p> : null}
            </Card>
          ))}
          <Pager testId="admin-audit-pager" page={data.page} pages={data.pages} total={data.total} onChange={setPage} />
        </>
      ) : <EmptyState testId="admin-audit-empty" icon={Receipt} title="No matching entries" />}
    </AdminPage>
  );
}
